import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../services/authentication/authContext';
import { canAccessWastePage } from '../../../../../services/accessManagement/accessManagement';
import { fetchWaste, WasteEntry } from '../../../../../services/waste/wasteService';
import { fetchCustomIngredientsByRestaurant, CustomIngredient } from '../../../../../services/customIngredients/customIngredientsService';
import { UtilizationReportInputs, UtilizationProductRow, UtilizationGroupMember } from '../../../../../utils/reports/utilizationReport';
import { computeWasteByProduct } from '../../../../../utils/reports/wasteByProduct';
import { enrichCostPriceListWithCurrentPrices } from '../../../../../utils/costPriceUtils';
import { inRange } from '../../../../../utils/reports/shared';
import UsedDishesDrawer from './UsedDishesDrawer';

/** Fetch logged waste only when a restaurant's product breakdown is opened. */
export const OrganizationProductDetail: React.FC<{
  product: UtilizationProductRow;
  inputs: UtilizationReportInputs;
  groupMembers?: UtilizationGroupMember[];
  periodLabel: string;
  onClose: () => void;
}> = ({ product, inputs, groupMembers, periodLabel, onClose }) => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const enabled = canAccessWastePage(backendUser, accountSettings);
  const restaurantId = inputs.currentRestaurantId;
  const [logged, setLogged] = useState<{ waste: WasteEntry[]; custom: CustomIngredient[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLogged(null);
    setFailed(false);
    void Promise.all([fetchWaste(restaurantId), fetchCustomIngredientsByRestaurant(restaurantId)])
      .then(([waste, custom]) => { if (!cancelled) setLogged({ waste, custom }); })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load reported waste for organization utilization', restaurantId, error);
        setFailed(true);
      });
    return () => { cancelled = true; };
  }, [restaurantId, enabled, attempt]);
  const reportedWaste = useMemo(() => {
    if (!enabled || !logged) return undefined;
    const wasteEntries = logged.waste.filter(w => inRange(new Date(w.wasteDate), inputs.range));
    if (!wasteEntries.length) return null;
    const enriched = enrichCostPriceListWithCurrentPrices(inputs.costPriceList, inputs.orders, {
      customIngredients: logged.custom, showExVat: true, allSubrecipes: inputs.subrecipesById,
      revenueHistory: inputs.revenue, currentRestaurantId: restaurantId, defaultWastePercentage: inputs.defaultWastePercentage,
    });
    return computeWasteByProduct({ wasteEntries, orders: inputs.orders, enriched,
      subrecipesById: inputs.subrecipesById, defaultWastePercentage: inputs.defaultWastePercentage,
      currentRestaurantId: restaurantId }).rows.find(row => row.key === product.key) ?? null;
  }, [enabled, logged, inputs, restaurantId, product.key]);
  return <UsedDishesDrawer product={product} onClose={onClose} periodLabel={periodLabel} groupMembers={groupMembers}
    reportedWaste={reportedWaste} notice={enabled && !logged ? <p role={failed ? 'alert' : 'status'} className="mb-3 text-sm text-muted dark:text-muted-dark">
      {t(failed ? 'reports.utilization.organization.wasteFailed' : 'common.loading')}{' '}
      {failed && <button type="button" className="underline" onClick={() => setAttempt(n => n + 1)}>{t('common.retry')}</button>}
    </p> : undefined} />;
};
